import {
	Component,
	ElementRef,
	ViewChild,
	EventEmitter,
	Output,
	AfterViewInit,
	ChangeDetectorRef,
	forwardRef,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BaseControlComponent } from '../base-control/base-control.component';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TextUtils } from '@mira-avant/mtlibs';
import { FieldLabelComponent } from '../../../ui/forms/field-label/field-label.component';
import { FieldErrorComponent } from '../../../ui/forms/field-error/field-error.component';
import { ZorroModule } from '../../../../modules/zorro.module';
import { NzColor } from 'ng-zorro-antd/color-picker';
import { MetabiUploaderService } from '../../../../services/metabi-uploader.service';
import { lastValueFrom } from 'rxjs';

@Component({
	selector: 'app-rich-text-control',
	standalone: true,
	imports: [FormsModule, ReactiveFormsModule, FieldLabelComponent, FieldErrorComponent, ZorroModule],
	templateUrl: './rich-text-control.component.html',
	styleUrl: './rich-text-control.component.scss',
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => RichTextControlComponent),
			multi: true,
		},
	],
})
export class RichTextControlComponent extends BaseControlComponent implements AfterViewInit, ControlValueAccessor {
	@ViewChild('editor', { static: false }) editor!: ElementRef<HTMLDivElement>;
	@Output('onFocus') public onFocusEmitter: EventEmitter<void> = new EventEmitter<void>();

	public bgColor: string | undefined;
	public fgColor: string | undefined;
	public resizeMode: boolean = false;
	private selectedRange: Range | null = null;
	private selectionStartOffset: number | null = null;
	private selectionEndOffset: number | null = null;
	private selectionStartContainer: Node | null = null;
	private selectionEndContainer: Node | null = null;
	private onTouched: () => void = () => {};
	private isDisabled: boolean = false;
	private _value: string = '';
	private resizingImage: HTMLImageElement | null = null;
	private initialWidth: number = 0;
	private initialHeight: number = 0;
	private aspectRatio: number = 0;
	private startX: number = 0;
	private isResizing: boolean = false;

	public override onChange: (value: string) => void = () => {};

	constructor(
		private cdr: ChangeDetectorRef,
		private uploadService: MetabiUploaderService
	) {
		super();
	}

	writeValue(value: string): void {
		this._value = value ?? '';

		if (this.editor?.nativeElement) {
			this.updateEditorElement(this._value);
		}

		this.cdr.markForCheck();
	}

	registerOnChange(fn: (value: string) => void): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		this.isDisabled = isDisabled;

		if (this.editor?.nativeElement) {
			this.editor.nativeElement.contentEditable = isDisabled ? 'false' : 'true';
			this.updateImageStyles();
			this.cdr.markForCheck();
		}
	}

	ngAfterViewInit() {
		if (this.editor) {
			requestAnimationFrame(() => {
				this.updateEditorElement(this._value);
				this.editor.nativeElement.contentEditable = this.isDisabled ? 'false' : 'true';
				this.setupImageResizeListeners();
				this.updateFormControl();
			});
		}
	}

	toggleResizeMode() {
		requestAnimationFrame(() => {
			this.resizeMode = !this.resizeMode;
			this.updateImageStyles();
			this.cdr.markForCheck();
		});
	}

	captureSelection() {
		const selection = window.getSelection();
		if (!selection || selection.rangeCount === 0) {
			this.selectedRange = null;
			this.selectionStartOffset = null;
			this.selectionEndOffset = null;
			this.selectionStartContainer = null;
			this.selectionEndContainer = null;
			return;
		}

		const range = selection.getRangeAt(0);
		this.selectedRange = range.cloneRange();
		this.selectionStartContainer = range.startContainer;
		this.selectionEndContainer = range.endContainer;
		this.selectionStartOffset = range.startOffset;
		this.selectionEndOffset = range.endOffset;
	}

	applyColor(type: 'color' | 'backgroundColor', color: { color: NzColor; format: string }) {
		if (this.isDisabled) {
			return;
		}

		const newColor: string = color.color.originalInput as string;
		if (type === 'backgroundColor') {
			this.bgColor = newColor;
		} else {
			this.fgColor = newColor;
		}

		this.editor.nativeElement.focus();

		const selection = window.getSelection();
		if (!selection) {
			console.error('No selection object available');
			return;
		}

		selection.removeAllRanges();

		let restored = false;
		if (
			this.selectedRange &&
			this.selectionStartContainer &&
			this.selectionEndContainer &&
			this.selectionStartOffset !== null &&
			this.selectionEndOffset !== null
		) {
			try {
				const range = document.createRange();
				if (
					this.editor.nativeElement.contains(this.selectionStartContainer) &&
					this.editor.nativeElement.contains(this.selectionEndContainer)
				) {
					range.setStart(this.selectionStartContainer, this.selectionStartOffset);
					range.setEnd(this.selectionEndContainer, this.selectionEndOffset);
					selection.addRange(range);
					restored = true;
				} else {
					console.warn('Selection containers are no longer valid in the DOM');
				}
			} catch (e) {
				console.warn('Failed to restore selection:', e);
			}
		}

		if (!restored || selection.rangeCount === 0 || selection.isCollapsed) {
			console.warn(
				'Selection could not be restored or is collapsed. Attempting fallback to current caret position.'
			);
			this.editor.nativeElement.focus();
			selection.removeAllRanges();
			const range = document.createRange();
			range.selectNodeContents(this.editor.nativeElement);
			selection.addRange(range);
		}

		if (!selection.isCollapsed) {
			try {
				const span = document.createElement('span');
				span.style[type === 'color' ? 'color' : 'backgroundColor'] = newColor;
				const currentRange = selection.getRangeAt(0);
				currentRange.surroundContents(span);
			} catch (e) {
				console.warn('Failed to surround selection with span, using execCommand fallback:', e);
				if (type === 'color') {
					document.execCommand('foreColor', false, newColor);
				} else {
					document.execCommand('hiliteColor', false, newColor);
				}
			}
		} else {
			document.execCommand('styleWithCSS', false, 'true');
			if (type === 'color') {
				document.execCommand('foreColor', false, newColor);
			} else {
				document.execCommand('hiliteColor', false, newColor);
			}
		}

		this.selectedRange = null;
		this.selectionStartOffset = null;
		this.selectionEndOffset = null;
		this.selectionStartContainer = null;
		this.selectionEndContainer = null;

		this.updateFormControl();
	}

	changeFontSize(direction: 'increase' | 'decrease') {
		if (this.isDisabled) {
			return;
		}

		this.captureSelection();
		const selection = window.getSelection();
		if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
			console.warn('No valid selection available');
			return;
		}

		this.editor.nativeElement.focus();
		selection.removeAllRanges();

		let restored = false;
		if (
			this.selectedRange &&
			this.selectionStartContainer &&
			this.selectionEndContainer &&
			this.selectionStartOffset !== null &&
			this.selectionEndOffset !== null
		) {
			try {
				const range = document.createRange();
				if (
					this.editor.nativeElement.contains(this.selectionStartContainer) &&
					this.editor.nativeElement.contains(this.selectionEndContainer)
				) {
					range.setStart(this.selectionStartContainer, this.selectionStartOffset);
					range.setEnd(this.selectionEndContainer, this.selectionEndOffset);
					selection.addRange(range);
					restored = true;
				} else {
					console.warn('Selection containers are no longer valid in the DOM');
				}
			} catch (e) {
				console.warn('Failed to restore selection:', e);
			}
		}

		if (!restored || selection.rangeCount === 0 || selection.isCollapsed) {
			console.warn('Selection could not be restored or is collapsed');
			return;
		}

		const range = selection.getRangeAt(0);
		const parentElement =
			range.commonAncestorContainer.nodeType === Node.TEXT_NODE
				? range.commonAncestorContainer.parentElement
				: (range.commonAncestorContainer as HTMLElement);

		// Check if the selection is within a span with a font-size
		let targetSpan: HTMLSpanElement | null = null;
		if (parentElement && parentElement.tagName === 'SPAN' && parentElement.style.fontSize) {
			targetSpan = parentElement as HTMLSpanElement;
		} else {
			// Check if the selection contains spans with font-size
			const fragment = range.cloneContents();
			const spans = fragment.querySelectorAll('span');
			if (spans.length > 0 && (spans[0] as HTMLSpanElement).style.fontSize) {
				targetSpan = range.startContainer.parentElement as HTMLSpanElement;
			}
		}

		let currentSize = 16; // Default font size
		if (targetSpan && targetSpan.style.fontSize) {
			currentSize = parseFloat(targetSpan.style.fontSize) || 16;
		} else {
			currentSize =
				parseFloat(window.getComputedStyle(parentElement || this.editor.nativeElement).fontSize) || 16;
		}

		const sizeChange = direction === 'increase' ? 2 : -2;
		const newSize = Math.max(8, currentSize + sizeChange); // Ensure minimum 8px

		try {
			if (targetSpan) {
				// Update existing span's font-size
				targetSpan.style.fontSize = `${newSize}px`;
			} else {
				// Create new span and wrap the selection
				const newSpan = document.createElement('span');
				newSpan.style.fontSize = `${newSize}px`;
				range.surroundContents(newSpan);
			}

			// Reselect the content
			selection.removeAllRanges();
			const newRange = document.createRange();
			newRange.selectNodeContents(targetSpan || range.commonAncestorContainer);
			selection.addRange(newRange);
		} catch (e) {
			console.warn('Failed to apply font size:', e);
			// Fallback to inserting the original content
			const tempDiv = document.createElement('div');
			tempDiv.appendChild(range.extractContents());
			range.insertNode(tempDiv);
		}

		this.selectedRange = null;
		this.selectionStartOffset = null;
		this.selectionEndOffset = null;
		this.selectionStartContainer = null;
		this.selectionEndContainer = null;

		this.updateFormControl();
	}

	executeCommand(command: string, value?: string) {
		if (this.isDisabled) {
			return;
		}

		if (command === 'removeFormat') {
			const editor = this.editor.nativeElement;
			const selection = window.getSelection();

			if (selection) {
				const range = document.createRange();
				range.selectNodeContents(editor);
				selection.removeAllRanges();
				selection.addRange(range);
				document.execCommand('removeFormat', false, undefined);
				const plainText = editor.textContent || '';
				editor.innerHTML = plainText;
			}
		} else {
			document.execCommand(command, false, value);
		}

		this.updateFormControl();
	}

	async handlePaste(event: ClipboardEvent) {
		if (this.isDisabled) {
			return;
		}

		event.preventDefault();

		const clipboardData = event.clipboardData;
		if (!clipboardData) {
			return;
		}

		const items = Array.from(clipboardData.items);
		const imageItem = items.find(item => item.type.startsWith('image/'));

		if (imageItem) {
			const file = imageItem.getAsFile();
			if (file) {
				await this.handleImageFile(file);
				return;
			}
		}

		let pastedData = clipboardData.getData('text/plain') || '';
		// Convert newlines to <br> tags
		pastedData = pastedData.replace(/\r\n|\n/g, '<br>');
		pastedData = TextUtils.sanitizeHtml(pastedData);

		if (this.isImageUrl(pastedData)) {
			this.insertImage(pastedData);
		} else {
			document.execCommand('insertHTML', false, pastedData);
		}

		this.updateFormControl();
	}

	handleInput() {
		if (this.isDisabled) {
			return;
		}

		this.updateImageStyles();
		this.updateFormControl();
	}

	handleBlur(event: Event) {
		if (this.isDisabled) {
			return;
		}
		this.onTouched();
		this.onBlurEmitter.emit(this._value);
		this.resizeMode = false;
		this.updateImageStyles();
		this.updateFormControl();
	}

	handleFocus(event: Event) {
		if (this.isDisabled) {
			return;
		}

		this.onFocusEmitter.emit();
	}

	private async handleImageFile(file: File) {
		try {
			const uploadResult = await lastValueFrom(this.uploadService.uploadFile(file));

			if (!Array.isArray(uploadResult.result) && uploadResult.result?.url) {
				this.insertImage(uploadResult.result.url);
			}
		} catch (error) {
			console.error('Image upload failed:', error);
			document.execCommand('insertHTML', false, '[Image upload failed]');
		}

		this.updateFormControl();
	}

	private isImageUrl(url: string): boolean {
		try {
			new URL(url);
			return /\.(jpeg|jpg|png|gif|bmp|webp|svg)$/i.test(url);
		} catch {
			return false;
		}
	}

	private insertImage(url: string) {
		const imgTag = TextUtils.sanitizeHtml(
			`<img src="${url}" alt="Pasted image" class="resizable-image" style="max-width: 100%; height: auto;">`
		);
		document.execCommand('insertHTML', false, imgTag);
		this.updateImageStyles();
	}

	private setupImageResizeListeners() {
		this.editor.nativeElement.addEventListener('mousedown', (event: MouseEvent) => {
			if (!this.resizeMode || this.isDisabled || event.button !== 0) {
				return;
			}
			const target = event.target as HTMLElement;
			if (target.tagName === 'IMG' && target.classList.contains('resizable-image')) {
				event.stopPropagation(); // Prevent drag in contenteditable
				this.startResize(event, target as HTMLImageElement);
			}
		});

		this.editor.nativeElement.addEventListener('mouseup', () => {
			if (this.isResizing) {
				this.stopResize();
			}
		});

		this.editor.nativeElement.addEventListener('mouseover', (event: MouseEvent) => {
			if (!this.resizeMode || this.isDisabled) {
				return;
			}
			const target = event.target as HTMLElement;
			if (target.tagName === 'IMG' && target.classList.contains('resizable-image')) {
				target.classList.add('resize-ready');
			}
		});

		this.editor.nativeElement.addEventListener('mouseout', (event: MouseEvent) => {
			if (!this.resizeMode || this.isDisabled) {
				return;
			}
			const target = event.target as HTMLElement;
			if (target.tagName === 'IMG' && target.classList.contains('resizable-image')) {
				target.classList.remove('resize-ready');
			}
		});
	}

	private updateImageStyles() {
		if (!this.editor || this.isDisabled) {
			return;
		}

		const images = this.editor.nativeElement.querySelectorAll('img.resizable-image');
		images.forEach((img: Element) => {
			const image = img as HTMLImageElement;
			image.draggable = !this.resizeMode; // Disable dragging in resizeMode
			if (this.resizeMode && !this.isResizing) {
				image.classList.add('resize-ready');
			} else {
				image.classList.remove('resize-ready');
			}
		});
	}

	private startResize(event: MouseEvent, img: HTMLImageElement) {
		event.preventDefault();
		this.resizingImage = img;
		this.startX = event.clientX;
		this.initialWidth = img.offsetWidth;
		this.initialHeight = img.offsetHeight;
		this.aspectRatio = this.initialWidth / this.initialHeight;
		this.isResizing = true;

		document.addEventListener('mousemove', this.resizeImage.bind(this));
	}

	private resizeImage(event: MouseEvent) {
		if (!this.resizingImage || !this.isResizing) {
			return;
		}

		const deltaX = event.clientX - this.startX;
		const newWidth = Math.max(50, this.initialWidth + deltaX); // Minimum width 50px
		const newHeight = newWidth / this.aspectRatio;

		this.resizingImage.style.width = `${newWidth}px`;
		this.resizingImage.style.height = `${newHeight}px`;

		this.updateFormControl();
	}

	private stopResize() {
		this.isResizing = false;
		this.resizingImage = null;
		document.removeEventListener('mousemove', this.resizeImage.bind(this));
		this.updateImageStyles();
		this.updateFormControl();
	}

	private updateFormControl() {
		if (this.editor) {
			let content = this.editor.nativeElement.innerHTML;
			content = TextUtils.sanitizeHtml(content);
			if (content === ' ' && this.editor.nativeElement.textContent?.trim()) {
				content = this.editor.nativeElement.textContent.trim();
			}
			this._value = content;
			this.onChange(this._value);
			this.cdr.markForCheck();
		}
	}

	private updateEditorElement(newValue: string) {
		if (this.editor) {
			this.editor.nativeElement.innerHTML = TextUtils.sanitizeHtml(newValue);
			const images = this.editor.nativeElement.querySelectorAll('img');
			images.forEach(img => img.classList.add('resizable-image'));
			this.updateImageStyles();
			this.cdr.markForCheck();
		}
	}
}
